/**
 * Taken tasks, stored on the server — and ONLY there.
 *
 * The log lives in `user_tasks` / `user_subtasks` (see
 * migrations/2026-08-04_tasks.sql): taking a task freezes its plan and its
 * price in a snapshot, marking a day goes through `app_submit_subtask`, and
 * points are always a SUM over the frozen prices of the steps actually done —
 * the same number on every device, because there is only one log. No
 * localStorage anywhere: a second copy of state is a second version of the
 * truth, which is exactly the bug this file replaced.
 *
 * The hook keeps the exact shape the screens always used: a synchronous `list`
 * plus fire-and-forget verbs. Mutations update the in-memory cache
 * optimistically and then reconcile against the server, so a tap feels instant
 * and the truth still wins.
 */
import React from 'react';
import { supabase } from '../lib/supabase';

/** ISO day, local time — the unit every check is keyed by. */
export const dayKey = (d: Date = new Date()): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * The inverse of `dayKey`, and the only correct way to turn one back into a Date.
 *
 * `new Date('2026-08-03')` is parsed as UTC midnight, so anywhere west of
 * Greenwich it renders as August 2 — a key written locally would be read back as
 * the previous day. Comparing two day keys needs no Date at all: ISO days sort
 * lexicographically, so `a >= b` is the cheaper and safer form.
 */
export const parseDayKey = (k: string): Date => {
    const [y, m, d] = k.split('-').map(Number);

    return new Date(y, m - 1, d);
};

export interface TaskEntry {
    done: boolean;
    /** Optional one-liner: what happened, in the user's words. */
    text?: string;
    /** 0–10 difficulty. Two readings of 8+ is the signal to shrink the task. */
    hard?: number;
    /** What got in the way, on a skipped day. */
    friction?: string[];
    /** The plan step this day paid — its price sits in the task's snapshot. */
    sub?: string;
}

/** One step's frozen worth, from `price_snapshot` at the moment of taking. */
export interface StepPrice {
    total: number;
    by_pattern: Record<string, number>;
    by_direction: Record<string, number>;
}

export interface TakenTask {
    /** Exercise code from the content payload. */
    code: string;
    /** Server row id (`user_tasks.id`). Empty while a take is still in flight. */
    id: string;
    startedAt: string;
    /** Target length in days. */
    days: number;
    /** One note per task, editable at any time — not one per day. */
    note: string;
    log: Record<string, TaskEntry>;
    /** Frozen step order — which subtask a new day pays into. */
    planOrder: string[];
    /** Frozen price per step. Points are read from here, never recomputed. */
    steps: Record<string, StepPrice>;
}

/* ── derived helpers, all off the reconstructed log ── */

/** Days with a `done` entry, counted against the target. */
export const doneCount = (t: TakenTask): number => Object.values(t.log || {}).filter((e) => e.done).length;

/** Consecutive done days ending today or yesterday; a single gap ends it. */
export function streakOf(t: TakenTask): number {
    let n = 0;
    const d = new Date();

    // Today not being logged yet must not zero a live streak, so start from
    // today and allow the first day to be missing.
    for (let i = 0; i < 400; i += 1) {
        const key = dayKey(d);
        const hit = t.log?.[key]?.done;

        if (hit) n += 1;
        else if (i > 0) break;
        d.setDate(d.getDate() - 1);
    }
    return n;
}

export const isDoneToday = (t: TakenTask): boolean => !!t.log?.[dayKey()]?.done;

/** Any record for today — done or an explicit skip. Both end the day's work. */
export const isLoggedToday = (t: TakenTask): boolean => !!t.log?.[dayKey()];

export const todayEntry = (t: TakenTask): TaskEntry | undefined => t.log?.[dayKey()];

/**
 * Index of today inside a Monday-first week. JS counts weeks from Sunday (0), so
 * the shift puts Monday at 0 and lines the data up with the DAY_KEYS labels.
 */
export const todayIndex = (): number => (new Date().getDay() + 6) % 7;

/**
 * Monday–Sunday of the current week, oldest first — drives the week dots.
 *
 * A rolling "last seven days" window would be off by the weekday: work done on a
 * Friday landed in the final slot, which is labelled Sunday.
 */
export function weekOf(t: TakenTask): boolean[] {
    const out: boolean[] = [];
    const d = new Date();

    d.setDate(d.getDate() - todayIndex());
    for (let i = 0; i < 7; i += 1) {
        out.push(!!t.log?.[dayKey(d)]?.done);
        d.setDate(d.getDate() + 1);
    }
    return out;
}

/* ── the store ─────────────────────────────────────── */

let cache: TakenTask[] = [];
let status: 'idle' | 'loading' | 'ready' = 'idle';
const listeners = new Set<(v: TakenTask[]) => void>();

function publish(): void {
    listeners.forEach((fn) => fn(cache));
}

const tz = (): string => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
        return 'UTC';
    }
};

function rowsToCache(utRows: any[], usRows: any[]): TakenTask[] {
    const byTake: Record<string, any[]> = {};

    usRows.forEach((s) => {
        (byTake[s.user_task_id] = byTake[s.user_task_id] || []).push(s);
    });

    return utRows.map((r) => {
        const log: Record<string, TaskEntry> = {};

        (byTake[r.id] || []).forEach((s) => {
            const inputs = s.inputs || {};

            log[s.done_on] = {
                done: !!s.done,
                sub: s.subtask_code,
                ...(inputs.text ? { text: inputs.text } : {}),
                ...(inputs.hard != null ? { hard: inputs.hard } : {}),
                ...(inputs.friction ? { friction: inputs.friction } : {}),
            };
        });

        return {
            code: r.task_code,
            id: r.id,
            startedAt: r.started_at,
            days: r.days,
            note: r.note || '',
            log,
            planOrder: r.plan_snapshot?.order || [],
            steps: r.price_snapshot?.steps || {},
        };
    });
}

async function fetchAll(): Promise<void> {
    const [ut, us] = await Promise.all([
        supabase.from('user_tasks').select('*').is('dropped_at', null),
        supabase.from('user_subtasks').select('*').is('voided_at', null),
    ]);

    if (ut.error || us.error) throw ut.error || us.error;
    cache = rowsToCache(ut.data || [], us.data || []);
    publish();
}

async function load(): Promise<void> {
    if (status !== 'idle') return;
    status = 'loading';
    try {
        const { data } = await supabase.auth.getSession();

        if (!data.session) {
            cache = [];
            status = 'ready';
            publish();
            return;
        }
        await fetchAll();
        status = 'ready';
    } catch {
        // Transient failure: fall back to an empty list and let the next mount retry.
        status = 'idle';
        cache = [];
        publish();
    }
}

/** Server truth after a mutation. Errors keep the optimistic state — better a
    stale number than a screen that flickers empty on a flaky network. */
function refresh(): void {
    fetchAll().catch(() => undefined);
}

// A login/logout swaps whose log this is; drop everything and start over.
supabase.auth.onAuthStateChange(() => {
    status = 'idle';
    cache = [];
    publish();
    load();
});

export function useTasks() {
    const [list, setList] = React.useState<TakenTask[]>(cache);

    React.useEffect(() => {
        listeners.add(setList);
        load();
        return () => {
            listeners.delete(setList);
        };
    }, []);

    const take = React.useCallback((code: string, opts: { days?: number; note?: string } = {}) => {
        if (cache.some((t) => t.code === code)) return;

        // Optimistic shell so the screen flips to "taken" immediately; the
        // server response fills in the id and the frozen snapshot.
        cache = [
            ...cache,
            {
                code,
                id: '',
                startedAt: new Date().toISOString(),
                days: opts.days ?? 7,
                note: opts.note || '',
                log: {},
                planOrder: [],
                steps: {},
            },
        ];
        publish();

        supabase
            .rpc('app_take_task', { p_task_code: code, p_days: opts.days ?? null, p_note: opts.note || '' })
            .then(({ error }) => {
                if (error) cache = cache.filter((t) => t.code !== code);
                refresh();
            });
    }, []);

    const drop = React.useCallback((code: string) => {
        const task = cache.find((t) => t.code === code);

        if (!task) return;
        cache = cache.filter((t) => t.code !== code);
        publish();
        if (!task.id) return;
        supabase
            .from('user_tasks')
            .update({ dropped_at: new Date().toISOString() })
            .eq('id', task.id)
            .then(() => refresh());
    }, []);

    /**
     * Records the day. The step is the first plan entry not yet done — which is
     * also the only step `app_submit_subtask` will accept — and re-marking an
     * already-logged today edits that same step instead of paying twice.
     */
    const mark = React.useCallback((code: string, entry: TaskEntry) => {
        const task = cache.find((t) => t.code === code);

        if (!task || !task.id) return;
        const today = dayKey();
        const doneSubs = new Set(
            Object.values(task.log)
                .filter((e) => e.done)
                .map((e) => e.sub),
        );
        const sub = task.log[today]?.sub || task.planOrder.find((s) => !doneSubs.has(s));

        if (!sub) return; // the plan is complete

        cache = cache.map((t) => (t.code === code ? { ...t, log: { ...t.log, [today]: { ...entry, sub } } } : t));
        publish();

        supabase
            .rpc('app_submit_subtask', {
                p_user_task_id: task.id,
                p_subtask_code: sub,
                p_tz: tz(),
                p_done: entry.done,
                p_inputs: {
                    ...(entry.text ? { text: entry.text } : {}),
                    ...(entry.hard != null ? { hard: entry.hard } : {}),
                    ...(entry.friction?.length ? { friction: entry.friction } : {}),
                },
            })
            .then(() => refresh());
    }, []);

    /** Removes today's record, so a mis-tap is undoable. Voids, never deletes. */
    const unmark = React.useCallback((code: string) => {
        const task = cache.find((t) => t.code === code);
        const today = dayKey();

        if (!task || !task.log[today]) return;
        cache = cache.map((t) => {
            if (t.code !== code) return t;
            const log = { ...t.log };

            delete log[today];
            return { ...t, log };
        });
        publish();
        if (!task.id) return;
        supabase
            .from('user_subtasks')
            .update({ voided_at: new Date().toISOString() })
            .eq('user_task_id', task.id)
            .eq('done_on', today)
            .is('voided_at', null)
            .then(() => refresh());
    }, []);

    const setNote = React.useCallback((code: string, note: string) => {
        const task = cache.find((t) => t.code === code);

        if (!task) return;
        cache = cache.map((t) => (t.code === code ? { ...t, note } : t));
        publish();
        if (!task.id) return;
        supabase
            .from('user_tasks')
            .update({ note })
            .eq('id', task.id)
            .then(() => undefined);
    }, []);

    return { list, take, drop, mark, unmark, setNote };
}
